import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { MessageSquare, MoreHorizontal } from "lucide-react";
import { Button, ButtonLink } from "./beui/Button";
import "./ContractHeaderActions.css";

export function ContractHeaderActions({ messageHref, messageLabel, primaryActions, actions }: {
  messageHref: string | null; messageLabel: string; primaryActions?: ReactNode; actions?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const root = useRef<HTMLDivElement>(null), trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!expanded) return;
    function outside(event: PointerEvent) {
      if (!(event.target instanceof Element) || root.current?.contains(event.target)
        || event.target.closest('dialog,[role="dialog"]')) return;
      setExpanded(false);
    }
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [expanded]);
  if (!messageHref && !primaryActions && !actions) return null;
  function controls() { return Array.from(root.current?.querySelectorAll<HTMLElement>('[data-contract-actions-panel] button:not(:disabled),[data-contract-actions-panel] a[href]') ?? []).filter(node => node.getClientRects().length); }
  return <div ref={root} role="group" aria-label="Workspace actions" className="contract-header-actions" onBlur={event => {
    const next = event.relatedTarget;
    if (next instanceof Element && !event.currentTarget.contains(next) && !next.closest('dialog,[role="dialog"]')) setExpanded(false);
  }} onKeyDown={event => {
    if (event.target instanceof Element && event.target.closest('dialog,[role="dialog"]') !== event.currentTarget.closest('dialog,[role="dialog"]')) return;
    if (event.key === "Escape" && expanded) {
      event.preventDefault(); event.stopPropagation(); setExpanded(false); trigger.current?.focus(); return;
    }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    if (!expanded) { setExpanded(true); requestAnimationFrame(() => { const items = controls(); (event.key === "ArrowUp" || event.key === "End" ? items[items.length - 1] : items[0])?.focus(); }); return; }
    const items = controls(), index = items.indexOf(document.activeElement as HTMLElement);
    const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (index + (event.key === "ArrowUp" ? -1 : 1) + items.length) % items.length;
    items[next]?.focus();
  }}>
    <Button ref={trigger} type="button" variant="outline" size="icon" className="contract-actions-toggle" aria-label={expanded ? "Hide actions" : "More actions"} aria-expanded={expanded} aria-controls={panelId} onClick={() => setExpanded(!expanded)}><MoreHorizontal className="h-5 w-5" aria-hidden="true" /></Button>
    {/* Keep source-owned dialogs and pending downloads mounted when the box closes. */}
    <div id={panelId} data-contract-actions-panel hidden={!expanded} className="contract-actions-panel">
      {messageHref && <Link href={messageHref} asChild><ButtonLink variant="ghost" className="contract-menu-message" aria-label={messageLabel}><MessageSquare className="h-4 w-4" aria-hidden="true" />Message</ButtonLink></Link>}
      <div className="contract-extra-actions">{primaryActions}{actions}</div>
    </div>
  </div>;
}
